import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setModelPrice } from '@/lib/api/generated/api';
import type { SetModelPriceRequest } from '@/lib/api/generated/model';
import { organizationKeys } from '@/features/organization-admin';
import { UsageError } from '@/features/workspace-usage';

import { llmProfileKeys } from '../queryKeys';

/**
 * Recording a rate for a model (spec 077 US2).
 *
 * The refusals carry more weight here than in the limit mutations, because this writes to
 * the numbers the organization's spending is judged by. Each status gets its own sentence:
 * a 409 is not a failure but a rule — rates are append-only, and the person needs to be
 * told what to do instead rather than invited to retry the same thing.
 */
function priceError(status: number): UsageError {
  if (status === 403) {
    return new UsageError(
      'forbidden',
      'Only an organization administrator can record a rate.',
    );
  }
  if (status === 409) {
    return new UsageError(
      'unavailable',
      "A rate for that model already starts on that past date and can't be replaced — add one from a later date instead. Resubmitting for today's date is allowed and overwrites it.",
    );
  }
  if (status === 422) {
    return new UsageError(
      'unavailable',
      'That rate sheet was refused. Every rate must be zero or more, quoted per million tokens.',
    );
  }
  return new UsageError('unavailable', 'Could not record the rate.');
}

export function useSetModelPrice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: SetModelPriceRequest) => {
      const response = await setModelPrice(payload);
      if (response.status === 201) return response.data;
      throw priceError(response.status);
    },
    // The same invalidation the limit mutations use, for a stronger reason: a new rate
    // changes the *cost* of runs already recorded, so the spend figures and the limit
    // proximity on the organization console are stale at once. Invalidating the whole
    // organization key is not laziness here — it is the actual blast radius.
    //
    // `llmProfileKeys.modelPrices()` is invalidated alongside it now that this mutation
    // lives in `llm-profiles`: the "current price" lookup the LLM Connections & Models
    // page reads is a different cache entry from anything under `organizationKeys`, and
    // without this a successful submission would leave that page showing stale state
    // until an unrelated refetch happened to occur (spec 092 FR-006a).
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
        queryClient.invalidateQueries({
          queryKey: llmProfileKeys.modelPrices(),
        }),
      ]),
  });
}
